/**
 * Category entity class
 */

import { getName, remove } from 'app/helpers/entity'

/**
 * CategoryFactory
 * returns Category entity class
 *
 * @class CategoryFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Category} class
 *
 * @ngdoc factory
 * @name mwApp.factory:CategoryFactory
 */
/* @ngInject */
export default function CategoryFactory (Entity, m2mMixin, upDownMixin) {
  /* === Category class === */

  /**
   * Category model
   *
   * @class Category
   * @classdesc Category model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.upDownMixin
   * @param {Object} [object] - initial object, containing Category entity fields
   * @return {models.Category} Category entity
   */
  function Category () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Category.prototype = Object.create(Entity.prototype)
  Category.prototype.constructor = Category

  /* === Category instance methods & fields === */

  Object.assign(Category.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Category#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      categorySet: 'CategorySet',
      channels: ['Channel'],
      parentCategory: 'Category',
      sortOrder: Number,
      subCategories: ['SubCategory']
    },

    /**
     * url template
     *
     * @memberof models.Category#
     * @type {String}
     */
    _urlTemplate: 'api/sets/{{::categorySet.id}}/categories/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Category#
     * @type {String}
     */
    _publicUrlTemplate: 'sets/{{::categorySet.id}}/categories/{{::id}}',

    /**
     * Get SubCategories names as string
     *
     * @memberof models.Category#
     * @return {String} SubCategories names list, separated by commas
     */
    _subCategoriesNames: function () {
      if (this.subCategories && this.subCategories.length > 0) {
        return this.subCategories.map(getName).join(', ')
      }
      return ''
    },

    /**
     * Get next sortOrder for new Category in array
     *
     * @protected
     * @memberof models.Category~
     * @param {models.Category[]} arr - array of categories
     * @return {Number} max+1 sortOrder from elements in array
     */
    __next_sort_order: function (arr) {
      return (arr && arr.length > 0)
        ? 1 + Math.max.apply(Math, arr.map(function (i) { return i.sortOrder }))
        : 0
    },

    /**
     * Save/update category, performing PUT/POST request to backend
     *
     * @memberof models.Category#
     * @return {Promise} promise
     * @override
     */
    _save: function (_mapFunc, _customUrl, _saveMsg, _updateMsg, categorySet) {
      var isNew = this._isNew()
      return Entity.prototype._save.apply(this, arguments)
        .then(
          () => {
            if (isNew && categorySet) {
              this.categorySet = categorySet
              this.subCategories = []

              // add Category to parent CategorySet.categories array
              this.categorySet.categories = this.categorySet.categories || []
              this.sortOrder = this.__next_sort_order(this.categorySet.categories)
              this.categorySet.categories.push(this)

              this._updateUrl()
            }
          }
        )
    },

    /**
     * Delete category, performing DELETE request to backend,
     * and removing category from parent CategorySet
     *
     * @memberof models.Category#
     * @return {Promise} promise
     * @override
     */
    _delete: function () {
      return Entity.prototype._delete.apply(this, arguments)
        .then(
          () => {
            if (this.categorySet && this.categorySet.categories && this.categorySet.categories.length > 0) {
              remove(this.categorySet.categories)(this)()
            }
          }
        )
    }

  }, m2mMixin('channels'), upDownMixin('sortOrder'))

  /* === Category static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Category, Entity)
  Category._name = 'Category'
  Category.init()

  // permissions
  Category.access = {
    view: 'viewTvChannelCategory viewRadioChannelCategory viewVodCategory viewTvShowCategory | | |',
    create: 'createTvChannelCategory createRadioChannelCategory createVodCategory createTvShowCategory | | |',
    edit: 'editTvChannelCategory editRadioChannelCategory editVodCategory editTvShowCategory | | |',
    delete: 'deleteTvChannelCategory deleteTvChannelCategory deleteVodCategory deleteTvShowCategory | | |'
  }

  return Category
}
